import _ from 'lodash';
import { useAnalytics } from 'use-analytics';
import { useDispatch, useSelector } from 'react-redux';
import { useMemo } from 'react';
import getBrochureTripId from '../helpers/getBrochureTripId';
import useBrochureOptionRemoval from './useBrochureOptionRemoval';
import { TravelTypes } from '../constants';
import { addToBrochure } from '../store/slices/brochure';
import isOutboundFlightLegExistsInBrochure from '../helpers/checkIfFlightItineraryExistsInBrochure';

const useHandleFlightAddToBrochure = ({
  tripId, flightItinerary, brochure,
  isViewingBrochure,
  isBrochurePage,
  currentBrochure,
  brochureOnBrochurePage,
  tripRoomSearchInput,
}) => {
  const { track } = useAnalytics();
  const dispatch = useDispatch();
  const {
    staterooms,
  } = useSelector((store) => store.tripSearch.searchObj);

  const id = useMemo(() => (
    getBrochureTripId(tripId, brochure, flightItinerary,
      tripRoomSearchInput || staterooms, TravelTypes.flight)
  ), [brochure, flightItinerary, staterooms, tripRoomSearchInput, tripId]);

  const findFlightSegmentByKeyAndDates = (tripSegments, flightKey) => _.find(tripSegments,
    (i) => i.itinerary.flightKey === flightKey
      && i.itinerary?.startDateTime === flightItinerary?.startDateTime
      && i.itinerary?.endDateTime === flightItinerary?.endDateTime);

  const handleSegmentRemoval = useBrochureOptionRemoval();

  const handleAddToBrochureClick = (event) => {
    if (event && event.type === 'keydown' && event.key !== 'Enter') return;
    if (!flightItinerary || !tripId) return;
    const flightKey = flightItinerary && flightItinerary.flightKey;
    const handleSegmentRemovalWrapper = (brochureId, brochureToUpdate, isSavedToDatabase) => {
      handleSegmentRemoval({
        brochureId,
        currentBrochure: brochureToUpdate,
        travelType: TravelTypes.flight,
        segmentKey: 'flightKey',
        segmentKeyValue: flightKey,
        findTripSegmentfn: findFlightSegmentByKeyAndDates,
        isSavedToDatabase,
        itinerary: flightItinerary,
        staterooms: tripRoomSearchInput,
      });
    };

    // Handle brochure page logic
    if (isBrochurePage && tripId) {
      const brochureToUpdate = JSON.parse(JSON.stringify(brochureOnBrochurePage));
      handleSegmentRemovalWrapper(tripId, brochureToUpdate, true);
      return;
    }

    if (isViewingBrochure && currentBrochure !== null && currentBrochure !== undefined) {
      const brochureId = brochure && brochure.id;
      const isSavedToDatabase = brochure && brochure.isSavedToDatabase;
      handleSegmentRemovalWrapper(brochureId, currentBrochure, isSavedToDatabase);
    }

    let updatedTrip = { tripSegments: [] };

    if (brochure && brochure.trips && brochure.trips[id]) {
      // Clone brochure, we cannot update brochure value directly
      updatedTrip = JSON.parse(JSON.stringify(brochure.trips[id]));
    }

    const tripSegment = findFlightSegmentByKeyAndDates(updatedTrip.tripSegments,
      flightKey, updatedTrip);
    const { segment, brochureId } = isOutboundFlightLegExistsInBrochure(brochure,
      flightItinerary.flightKey) || {};
    if (!tripSegment && !segment) {
      updatedTrip.tripSegments.push({ itinerary: flightItinerary });
    } else if (!tripSegment && segment) { // Remove flight from brochure in outbound leg
      if (!updatedTrip.tripSegments.length && brochureId) {
        updatedTrip = JSON.parse(JSON.stringify(brochure.trips[brochureId]));
      }
      _.remove(updatedTrip.tripSegments, (ts) => ts.itinerary.flightKey
      === segment.itinerary.flightKey);
    } else {
      _.remove(updatedTrip.tripSegments, (ts) => ts.itinerary.flightKey === flightKey);
    }
    dispatch(addToBrochure({
      tripId: (!tripSegment && segment)
        ? brochureId : id,
      updatedTrip,
      staterooms,
    }));
    track('FlightAddUpdateToBrochure.handleAddToBrochureClick');
    // Remove the outbound flight leg if the inbound leg is added to brochure.
    const flightKeys = flightItinerary.flightSegments.length > 1 ? flightKey.split('|') : null;
    const brochureTripIds = Object.keys(brochure.trips);
    if (flightKeys && flightKeys.length > 1) {
      brochureTripIds.forEach((brochureTripId) => {
        const originalTrip = brochure.trips[brochureTripId];
        // Only filter if there is a matching segment to remove
        const filteredSegments = originalTrip.tripSegments.filter((ts) => {
          const { itinerary } = ts;
          return !(itinerary.mode === TravelTypes.flight
                   && itinerary.flightSegments.length < flightItinerary.flightSegments
                     .length && flightKeys.includes(itinerary.flightKey));
        });
        if (filteredSegments.length !== originalTrip.tripSegments.length) {
          dispatch(addToBrochure({
            tripId: brochureTripId,
            updatedTrip: { ...originalTrip, tripSegments: filteredSegments },
            staterooms,
          }));
          track('FlightAddUpdateToBrochure.handleAddToBrochureClick');
        }
      });
    }
  };
  return handleAddToBrochureClick;
};

export default useHandleFlightAddToBrochure;
